import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Lock, 
  LogOut, 
  Download, 
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Clock,
  User,
  FileText,
  Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRequests = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Check if already authenticated (stored in sessionStorage)
  useEffect(() => {
    const storedPassword = sessionStorage.getItem('adminPassword');
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
      fetchRequests(storedPassword);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.get(`${API}/help-requests/verify-password`, {
        headers: { 'X-Admin-Password': password }
      });
      
      sessionStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
      fetchRequests(password);
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid password');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
    setPassword('');
    setRequests([]);
  };

  const fetchRequests = async (adminPassword) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/help-requests`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/help-requests/export/csv`, {
        headers: { 'X-Admin-Password': password },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `help_requests_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHelpTypeLabel = (type) => {
    const labels = {
      'doctor-appointment': 'Doctor Appointment',
      'wellbeing-checkin': 'Wellbeing Check-in',
      'grocery-medicine': 'Grocery & Medicine',
      'paperwork-bills': 'Paperwork & Bills',
      'errands': 'Light Errands',
      'family-coordination': 'Family Coordination',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  const getHelpForLabel = (value) => {
    const labels = {
      'self': 'Self',
      'parent': 'Parent',
      'relative': 'Relative'
    };
    return labels[value] || value;
  };

  const getContactMethodLabel = (value) => {
    const labels = {
      'call': 'Phone Call',
      'whatsapp': 'WhatsApp',
      'email': 'Email'
    };
    return labels[value] || value;
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-20 bg-[#FFF9F2]">
        <Toaster position="top-center" richColors />
        <div className="max-w-md mx-auto px-4 md:px-6 py-20">
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#232323] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="heading-2">Admin Access</h1>
              <p className="text-sm text-[#666] mt-2">
                Enter password to view help requests
              </p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="form-input text-center"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Access Dashboard'
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20 bg-[#FFF9F2]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-[#eee] sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="heading-2">Help Requests</h1>
              <p className="text-sm text-[#666]">
                {requests.length} total request{requests.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchRequests(password)}
                disabled={isLoading}
                className="btn-secondary gap-2 py-2 px-4"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExportCSV}
                disabled={isExporting || requests.length === 0}
                className="btn-secondary gap-2 py-2 px-4"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export CSV
              </button>
              <button
                onClick={handleLogout}
                className="btn-secondary gap-2 py-2 px-4 text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {isLoading && requests.length === 0 ? (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#666]" />
            <p className="mt-4 text-[#666]">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 mx-auto text-[#ccc]" />
            <p className="mt-4 text-[#666]">No help requests yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request, index) => (
              <motion.div
                key={request.id}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Main Info */}
                  <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Name & Contact */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-[#666]" />
                        <span className="font-semibold text-[#232323]">{request.full_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#666] mb-1">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${request.phone}`} className="hover:text-[#232323]">
                          {request.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#666]">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${request.email}`} className="hover:text-[#232323] truncate max-w-[200px]">
                          {request.email}
                        </a>
                      </div>
                    </div>

                    {/* Location & Request Type */}
                    <div>
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <MapPin className="w-4 h-4 text-[#666]" />
                        <span className="text-[#232323]">{request.city}</span>
                      </div>
                      <div className="mb-1">
                        <span className="text-xs text-[#666] uppercase tracking-wide">Help Type</span>
                        <p className="text-sm font-medium text-[#232323]">
                          {getHelpTypeLabel(request.help_type)}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-[#666] uppercase tracking-wide">For</span>
                        <p className="text-sm text-[#232323]">{getHelpForLabel(request.help_for)}</p>
                      </div>
                    </div>

                    {/* Contact Preference & Timestamp */}
                    <div>
                      <div className="mb-2">
                        <span className="text-xs text-[#666] uppercase tracking-wide">Preferred Contact</span>
                        <p className="text-sm font-medium text-[#232323]">
                          {getContactMethodLabel(request.contact_method)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#666]">
                        <Clock className="w-3 h-3" />
                        {formatDate(request.timestamp)}
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                {request.notes && (
                  <div className="mt-4 pt-4 border-t border-[#eee]">
                    <span className="text-xs text-[#666] uppercase tracking-wide">Notes</span>
                    <p className="text-sm text-[#353535] mt-1">{request.notes}</p>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="mt-4 pt-4 border-t border-[#eee] flex flex-wrap gap-2">
                  <a
                    href={`tel:${request.phone}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#232323] bg-[#f5f5f5] px-3 py-1.5 rounded-full hover:bg-[#eee] transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    Call
                  </a>
                  <a
                    href={`https://wa.me/${request.phone.replace(/\s/g, '').replace(/^\+/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#25D366] px-3 py-1.5 rounded-full hover:bg-[#20bd5a] transition-colors"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:${request.email}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#232323] bg-[#f5f5f5] px-3 py-1.5 rounded-full hover:bg-[#eee] transition-colors"
                  >
                    <Mail className="w-3 h-3" />
                    Email
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRequests;
